import { createSelector } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { blotterSlice } from '@/store/blotter/slice.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { Order } from '@/store/orders/state.ts';
import { isStopLossOrder } from '@/store/orders/stopLoss/helpers.ts';
import type { Trigger } from '@/store/orders/stopLoss/state.ts';
import type { ExpiryDate, GoodTillCancelled } from '@/store/orders/takeProfitOrder/state.ts';
import type { AppState } from '@/store/store.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

export const selectStopLossTile = createSelector(
  (state: AppState, tileId: string) => state.orders[tileId] as Order | undefined,
  (order) => (isStopLossOrder(order) ? order : undefined),
);
export const selectOrderEntryByIdFromBlotter = createSelector(
  (state: AppState, orderId: string) => blotterSlice.selectors.getOrderEntryById(state, orderId),
  (order) => order,
);

export const selectStopLossErrors = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return undefined;
    }

    return order.properties.errors;
  },
);

export const selectExpiryStopLoss = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (order): GoodTillCancelled | ExpiryDate | undefined => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return undefined;
    }

    return order.properties.expiry;
  },
);

export const selectNotionalDataStopLoss = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return undefined;
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      notional: order.properties.notional,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectNotionalCurrencyIndexStopLoss = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return undefined;
    }

    return order.properties.notionalCurrencyIndex;
  },
);

export const selectTradeIdFromStopLossTile = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (tile, tileId) => {
    if (isNotDefined(tile) || tile.type !== 'StopLossOrder') {
      return undefined;
    }

    if (
      tile.properties.status.type === 'creating' ||
      tile.properties.status.type === 'failed' ||
      tile.properties.status.type === 'pending'
    ) {
      console.warn(`[StopLossOrderEditionTile] Tile with id ${tileId} is in creating state, no data available.`);
      return undefined;
    }

    return tile.properties.status.tradeId;
  },
);

export const selectTriggerStopLoss = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (order): Trigger | undefined => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return undefined;
    }

    return order.properties.trigger;
  },
);

export const selectLimitPriceStopLoss = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return undefined;
    }

    return order.properties.limitPrice;
  },
);

export const selectClientWayStopLossData = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return undefined;
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      clientWay: order.properties.clientWay,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectNotionalStopLoss = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return {};
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      notional: order.properties.notional,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectNotificationEmailsStopLoss = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (state: AppState) => state.userPreferences,
  (order, userPreference) => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return [];
    }

    if (isDefined(order.properties.notificationEmails)) {
      return order.properties.notificationEmails;
    }

    return userPreference.splitNotifications ? userPreference.splitNotificationsEmailsOrders : userPreference.emails;
  },
);

export const selectSpotStopLossData = createSelector(
  (state: AppState, tileId: string) => selectStopLossTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (state: AppState, tileId: string) => {
    const order = selectStopLossTile(state, tileId);

    if (
      isNotDefined(order) ||
      order.type !== 'StopLossOrder' ||
      isNotDefined(order.properties.currencyPair?.tiering) ||
      isNotDefined(order.properties.currencyPair?.currency1) ||
      isNotDefined(order.properties.currencyPair?.currency2)
    ) {
      return undefined;
    }

    const currencyPair = `${order.properties.currencyPair.currency1}/${order.properties.currencyPair.currency2}`;
    const espStreamId = getEspStreamId(currencyPair, order.properties.currencyPair.tiering);

    return state.esp.streams[espStreamId];
  },
  (order, tileId, espPrice) => {
    if (isNotDefined(order) || order.type !== 'StopLossOrder') {
      return undefined;
    }

    if (isNotDefined(order.properties.currencyPair?.tiering)) {
      console.warn(`[StopLossOrder] Tile with id ${tileId} has no tiering defined.`);
      return undefined;
    }

    return {
      spot:
        espPrice?.status === 'PRICED'
          ? convertPointsToRate(espPrice.midRate, espPrice.currencyPairPrecision)
          : undefined,
      digitCount: espPrice?.status === 'PRICED' ? espPrice.currencyPairPrecision - 1 : undefined,
    };
  },
);
